import { Loader2, Pause, Play } from "lucide-react";
import type { MouseEvent } from "react";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/v2/components/ui/tooltip";
import { cn } from "@/v2/lib/utils";
import { canTimeEntry, useEntriesTimer } from "./EntriesTimerContext";
import type { Entry } from "./normalize";

/**
 * Per-row timer toggle, rendered in the trailing `timer` column. Hidden on the
 * row until hover (via the row's `group/row`), unless this row holds the active
 * timer — then it stays visible. Reads the single active timer from context so
 * every row stays in sync. Only tracked/reviewed rows show it; exported/deleted
 * render nothing to preserve density.
 *
 * There is no stop — pause is the finish control (pause icon, stop behavior): it
 * persists the time and unbinds. So the active row shows a pause button; other
 * rows start/switch. To add more time to an entry later, start it again.
 */
export function RowTimerControl({ entry }: { entry: Entry }) {
	const { activeEntryId, isBusy, startForEntry, pause } = useEntriesTimer();

	if (!canTimeEntry(entry)) return null;

	const isActive = activeEntryId === entry.id;
	const timerRunsElsewhere = activeEntryId !== null && !isActive;

	const label = isActive
		? "Pause timer"
		: timerRunsElsewhere
			? "Switch timer to this entry"
			: "Start timer for this entry";

	const handleClick = (event: MouseEvent) => {
		event.stopPropagation(); // don't trigger the row's select/edit click
		if (isBusy) return;
		if (isActive) pause();
		else startForEntry(entry);
	};

	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>
					<button
						type="button"
						aria-label={label}
						aria-pressed={isActive}
						disabled={isBusy && !isActive}
						onClick={handleClick}
						onMouseDown={(event) => event.stopPropagation()}
						className={cn(
							"relative flex size-7 items-center justify-center rounded-md transition-colors",
							"focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
							isActive
								? "bg-ring text-background"
								: cn(
										"text-muted-foreground hover:bg-muted hover:text-foreground",
										timerRunsElsewhere && "text-ring",
									),
							isActive
								? "opacity-100"
								: "opacity-0 group-hover/row:opacity-100",
							isBusy && !isActive && "cursor-default opacity-40",
						)}
					>
						{isBusy && isActive ? (
							<Loader2 className="size-3.5 motion-safe:animate-spin" />
						) : isActive ? (
							<Pause className="size-3.5" />
						) : (
							<Play className="size-3.5 fill-current" />
						)}
					</button>
				</TooltipTrigger>
				<TooltipContent side="left">{label}</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}
